import { formatCurrency } from '@/libs/antd/formater';
import {
	CalculateSimulationResult,
	SaveSimulationRequest,
} from '@/libs/fleet-api/services/simulation';
import { Col, Descriptions, Row, Table } from 'antd';
import { FC } from 'react';

const SimulationPreviewContent: FC<{
	simulationResult: CalculateSimulationResult;
	simulationRequest?: SaveSimulationRequest;
	simulationId?: number;
}> = ({ simulationResult }) => {
	// const customerData = simulationResult;
	const stepsData = simulationResult.stepCreditSimulation;
	const fixedData = simulationResult.fixedCreditSimulation;

	const multiColumns = (values: (string | number | undefined)[]) => (
		// Use a Row as the flex container. Gutter adds space between columns.
		<Row
			gutter={16}
			align="middle"
		>
			{values.map((value, index) => (
				// The key is to use the `flex` prop on Col.
				// `flex={1}` makes each column take up an equal amount of space.
				<Col
					key={index}
					flex={1}
				>
					{value}
				</Col>
			))}
		</Row>
	);

	const stepsStyles = {
		base: {
			contentStyle: {
				textAlign: 'right',
			},
			span: 3,
		},
		header: {
			labelStyle: {
				fontWeight: 'bold',
				backgroundColor: '#E8F1FF',
				fontSize: '16px',
				// This is the key change to remove the divider
				borderInlineEnd: 'none',
			},
			contentStyle: {
				textAlign: 'right',

				backgroundColor: '#E8F1FF',
			},
		},
		highlight: {
			labelStyle: {
				fontSize: '16px',
				backgroundColor: '#fffbe6',
				fontWeight: 'bold',
			},
			contentStyle: {
				textAlign: 'right',

				fontSize: '16px',
				backgroundColor: '#fffbe6',
				fontWeight: 'bold',
			},
		},
	};

	const fixedStyles = {
		base: {
			span: 3,
			contentStyle: {
				textAlign: 'right',
			},
			labelStyle: {
				fontWeight: 'bold',
			},
		},
		header: {
			labelStyle: {
				fontWeight: 'bold',
				backgroundColor: '#E8F1FF',
				fontSize: '16px',
				// This is the key change to remove the divider
				borderInlineEnd: 'none',
			},
			contentStyle: {
				backgroundColor: '#E8F1FF',
				textAlign: 'right',
			},
		},
		highlight: {
			labelStyle: {
				fontSize: '16px',
				backgroundColor: '#fffbe6',
				fontWeight: 'bold',
				borderInlineEnd: 'none',
			},
			contentStyle: {
				textAlign: 'right',
				fontSize: '16px',
				backgroundColor: '#fffbe6',
				fontWeight: 'bold',
			},
		},
	};

	const stepsTypeItems = [
		// 1. This is your new header item
		{
			key: '0',
			label: 'Detail',
			...stepsStyles.base,
			...stepsStyles.header,
		},
		// 2. Add ...stepsStyles.base to all other items to ensure they take a full row
		{
			key: '1',
			label: 'Nama Lessee',
			children: stepsData?.lesseeName,
			...stepsStyles.base,
		},
		{
			key: '2',
			label: 'Nama PIC Lessee',
			children: stepsData?.lesseePicName,
			...stepsStyles.base,
		},
		{
			key: '3',
			label: 'Jenis Unit',
			children: stepsData?.unit,
			...stepsStyles.base,
		},
		{
			key: '4',
			label: 'Total Unit',
			children: stepsData?.unit,
			...stepsStyles.base,
		},
		{
			key: '5',
			label: 'Tipe Transaksi',
			children: stepsData?.transactionType,
			...stepsStyles.base,
		},
		{
			key: '6',
			label: 'Mata Uang',
			children: stepsData?.currency,
			...stepsStyles.base,
		},
		{
			key: '7',
			label: 'Bunga Eff.',
			children: stepsData?.effectiveRatePercentage + ' %',
			...stepsStyles.base,
		},
		{ key: '8', label: 'Tenor', children: '36 Bulan', ...stepsStyles.base },
		{
			key: '9',
			label: 'Siklus Pembayaran',
			children: stepsData?.paymentCycle,
			...stepsStyles.base,
		},
		{
			key: '10',
			label: 'O/S Principal',
			children: stepsData?.outstandingPrincipalAmount,
			...stepsStyles.base,
		},
		{
			key: '11',
			label: 'Nilai Pembiayaan',
			children: stepsData?.financingAmount,
			...stepsStyles.base,
		},
		{
			key: '12',
			label: 'Biaya Admin',
			children: stepsData?.administrationFee,
			...stepsStyles.base,
		},
		{
			key: '13',
			label: 'Biaya Asuransi',
			children: stepsData?.insuranceAmount,
			...stepsStyles.base,
		},
		{
			key: '14',
			label: 'Biaya Provisi',
			children: stepsData?.provisionFee,
			...stepsStyles.base,
		},
		{
			key: '15',
			label: 'Biaya Angsuran Pertama',
			children: stepsData?.firstInstallmentAmount,
			...stepsStyles.base,
		},
		{
			key: '16',
			label: 'Total Bayar Pertama ke MTF',
			children: stepsData?.firstPaymentAmount,
			...stepsStyles.base,
		},
		{
			key: '17',
			label: 'Total Bayar DP Murni',
			children: stepsData?.netDownPaymentAmount,
			...stepsStyles.base,
			labelStyle: { backgroundColor: '#fffbe6', fontWeight: 'bold' },
			contentStyle: { backgroundColor: '#fffbe6', fontWeight: 'bold' },
		},
		{
			key: '18',
			label: 'Komponen Angsuran Per Tahun',
			...stepsStyles.base,
			...stepsStyles.header,
		},
		...(stepsData?.componentInstallments || []).map((installment) => ({
			key: `component-installment-${installment.tenor}`,
			label: `Tahun Ke-${installment.tenor}`,
			span: 2,

			children: (
				<Descriptions
					layout="horizontal"
					column={1}
					size="small"
					contentStyle={{ justifyContent: 'flex-end', display: 'flex' }}
				>
					<Descriptions.Item label="">
						{installment.installmentPercentage}
					</Descriptions.Item>
					<Descriptions.Item label="">
						{installment.installmentAmount}
					</Descriptions.Item>
					<Descriptions.Item label="">
						{installment.installmentAmountInMaxUnit}
					</Descriptions.Item>
				</Descriptions>
			),
		})),
	];

	const stepsTypeInstallmentMatrixHeader = [
		{
			key: 'installment-matrix-header',
			label: 'Tabel Perhitungan Angsuran',
			...stepsStyles.base,
			...stepsStyles.header,
		},
	];

	const fixedTypeItems = [
		{
			key: '0',
			label: 'Detail',
			...fixedStyles.base,
			...fixedStyles.header,
		},
		{
			key: '1',
			label: 'Merk/Jenis',
			children: fixedData?.assetType,
			...fixedStyles.base,
		},
		{
			key: '2',
			label: 'Dealer',
			children: fixedData?.dealerSupplier,
			...fixedStyles.base,
		},
		{
			key: '3',
			label: 'Tipe Pembiayaan',
			children: fixedData?.paymentType,
			...fixedStyles.base,
		},
		{
			key: '4',
			label: 'Tipe Asuransi',
			children: fixedData?.insuranceType,
			...fixedStyles.base,
		},
		{
			key: '5',
			label: 'Hasil Skema Pembiayaan',
			...fixedStyles.base,
			...fixedStyles.header,
		},
		{
			key: '6',
			label: 'Jenis Pembayaran',
			children: fixedData?.paymentType,
			...fixedStyles.base,
		},
		{
			key: '7',
			label: 'Tenor (Bulan)',
			children: fixedData?.tenorInMonth,
			...fixedStyles.base,
		},
		{
			key: '8',
			label: 'Bunga',
			children: fixedData?.interestPercentage + '%',
			...fixedStyles.base,
		},

		{
			key: '9',
			label: 'Provisi',
			children: fixedData?.provisionPercentage + '%',
			...fixedStyles.base,
		},

		{
			key: '10',
			label: 'Asuransi',
			children: fixedData?.insuranceType,
			...fixedStyles.base,
		},
		{
			key: '11',
			label: 'Hasil Skema Asuransi',
			...fixedStyles.base,
			...fixedStyles.header,
		},
		...(fixedData?.insuranceSchemes || []).map((i, idx) => ({
			key: `fixed-data-insurance-${idx + 1}`,
			label: `Asuransi Tahun ${idx + 1}`,
			children: i.coverageType,
			...fixedStyles.base,
		})),
		{
			key: '13',
			label: 'Hasil Skema Kredit',
			...fixedStyles.base,
			...fixedStyles.header,
			children: multiColumns(
				fixedData?.creditSchemes?.map((i) => i?.unit) || [],
			),
		},
		{
			key: '14',
			label: 'OTR',
			children: multiColumns(
				fixedData?.creditSchemes?.map((i) => formatCurrency(i?.otrAmount)) ||
					[],
			),
			...fixedStyles.base,
		},

		{
			key: '15',
			label: 'Asuransi Full',
			children: multiColumns(
				fixedData?.creditSchemes?.map((i) =>
					formatCurrency(i?.fullInsuranceAmount),
				) || [],
			),
			...fixedStyles.base,
		},
		{
			key: '16',
			label: 'Down Payment',
			children: multiColumns(
				fixedData?.creditSchemes?.map((i) =>
					formatCurrency(i?.downPaymentAmount),
				) || [],
			),
			...fixedStyles.base,
		},
		{
			key: '17',
			label: 'Pokok Hutang',
			children: multiColumns(
				fixedData?.creditSchemes?.map((i) =>
					formatCurrency(i?.principalAmount),
				) || [],
			),
			...fixedStyles.base,
		},
		{
			key: '18',
			label: 'Bunga',
			children: multiColumns(
				fixedData?.creditSchemes?.map((i) =>
					formatCurrency(i?.interestAmount),
				) || [],
			),
			...fixedStyles.base,
		},
		{
			key: '19',
			label: 'Total AR',
			children: multiColumns(
				fixedData?.creditSchemes?.map((i) =>
					formatCurrency(i?.totalInstallmentReceivableAmount),
				) || [],
			),
			...fixedStyles.base,
		},
		{
			key: '20',
			label: 'Angsuran',
			children: multiColumns(
				fixedData?.creditSchemes?.map((i) =>
					formatCurrency(i?.totalInstallmentAmount),
				) || [],
			),
			...fixedStyles.base,
			...fixedStyles.highlight,
		},
		// PEMBAYARAN PERTAMA
		{
			key: '21',
			label: 'Pembayaran Pertama',
			...fixedStyles.base,
			...fixedStyles.header,
			children: multiColumns(
				fixedData?.firstPayments?.map((i) => formatCurrency(i?.unit)) || [],
			),
		},
		{
			key: '22',
			label: 'Down Payment',
			children: multiColumns(
				fixedData?.firstPayments?.map((i) =>
					formatCurrency(i?.downPaymentAmount),
				) || [],
			),
			...fixedStyles.base,
		},

		{
			key: '23',
			label: 'Asuransi',
			children: multiColumns(
				fixedData?.firstPayments?.map((i) =>
					formatCurrency(i?.insuranceAmount),
				) || [],
			),
			...fixedStyles.base,
		},
		{
			key: '24',
			label: 'Administrasi',
			children: multiColumns(
				fixedData?.firstPayments?.map((i) =>
					formatCurrency(i?.administrationAmount),
				) || [],
			),
			...fixedStyles.base,
		},
		{
			key: '25',
			label: 'Fidusia',
			children: multiColumns(
				fixedData?.firstPayments?.map((i) => formatCurrency(i?.fiduciaryFee)) ||
					[],
			),
			...fixedStyles.base,
		},
		{
			key: '26',
			label: 'Provisi + Biaya Layanan',
			children: multiColumns(
				fixedData?.firstPayments?.map((i) =>
					formatCurrency(i?.provisionFee + i.servicingFee),
				) || [],
			),
			...fixedStyles.base,
		},
		{
			key: '27',
			label: 'Provisi',
			children: multiColumns(
				fixedData?.firstPayments?.map((i) => formatCurrency(i?.provisionFee)) ||
					[],
			),
			...fixedStyles.base,
		},
		{
			key: '28',
			label: 'Biaya Layanan',
			children: multiColumns(
				fixedData?.firstPayments?.map((i) => formatCurrency(i?.servicingFee)) ||
					[],
			),
			...fixedStyles.base,
		},
		{
			key: '29',
			label: 'Angsuran Ke I',
			children: multiColumns(
				fixedData?.firstPayments?.map((i) =>
					formatCurrency(i?.firstInstallmentAmount),
				) || [],
			),
			...fixedStyles.base,
		},
		{
			key: '30',
			label: 'Total Bayar Pertama',
			children: multiColumns(
				fixedData?.firstPayments?.map((i) =>
					formatCurrency(i?.totalFirstPayment),
				) || [],
			),
			...fixedStyles.base,
			...fixedStyles.highlight,
		},
	];

	// Helper to align text based on content
	const getTextAlign = (child: unknown) => {
		if (
			typeof child === 'number' ||
			(typeof child === 'string' &&
				/[\d.,]/.test(child) &&
				!/[a-zA-Z]/.test(child))
		) {
			return 'right';
		}
		return 'left';
	};

	return (
		<div>
			{stepsData !== null ? (
				<>
					<Descriptions
						bordered
						// Set the base column count to 2
						column={3}
						labelStyle={{ width: '50%', backgroundColor: '#ffffff' }}
						contentStyle={{ backgroundColor: '#ffffff' }}
					>
						{stepsTypeItems.map((item) => (
							<Descriptions.Item
								key={item.key}
								label={item.label}
								span={'span' in item ? item?.span : undefined} // Assign the span from the item object
								labelStyle={'labelStyle' in item ? item.labelStyle : undefined}
								contentStyle={{
									// @ts-expect-error type
									textAlign: getTextAlign(
										'children' in item ? item.children : undefined,
									),
									...('contentStyle' in item ? item.contentStyle : {}),
								}}
							>
								{'children' in item ? item.children : undefined}
							</Descriptions.Item>
						))}
					</Descriptions>

					{/* INSTALLMENT MATRIX */}
					{/* HEADER */}
					<Descriptions
						bordered
						column={1}
						labelStyle={{ width: '50%', backgroundColor: '#ffffff' }}
						contentStyle={{ backgroundColor: '#ffffff' }}
					>
						{stepsTypeInstallmentMatrixHeader.map((item) => (
							<Descriptions.Item
								key={item.key}
								label={item.label}
								span={'span' in item ? item?.span : undefined} // Assign the span from the item object
								labelStyle={'labelStyle' in item ? item.labelStyle : undefined}
								contentStyle={{
									// @ts-expect-error type
									textAlign: getTextAlign(
										'children' in item ? item.children : undefined,
									),
									...('contentStyle' in item ? item.contentStyle : {}),
								}}
							/>
						))}
					</Descriptions>
					{/* BODY */}
					<Table
						dataSource={stepsData.installments}
						columns={[
							{
								title: 'Month',
								align: 'center',
								render: (_, record) => formatCurrency(record.tenorInMonth),
							},
							{
								title: 'PMT',
								align: 'center',
								render: (_, record) => formatCurrency(record.installmentAmount),
							},
							{
								title: 'PMT Rounded',
								align: 'center',
								render: (_, record) =>
									formatCurrency(record.roundedInstallmentAmount),
							},
							{
								title: 'Interest',
								align: 'center',
								render: (_, record) => formatCurrency(record.interestAmount),
							},
							{
								title: 'Principal',
								align: 'center',
								render: (_, record) => formatCurrency(record.principalAmount),
							},
							{
								title: 'O/S Principal',
								align: 'center',
								render: (_, record) =>
									formatCurrency(record.outstandingPrincipalAmount),
							},
						]}
					/>
				</>
			) : fixedData !== null ? (
				<Descriptions
					bordered
					// Set the base column count to 2
					column={3}
					labelStyle={{ width: '50%', backgroundColor: '#ffffff' }}
					contentStyle={{ backgroundColor: '#ffffff' }}
				>
					{fixedTypeItems.map((item) => (
						<Descriptions.Item
							key={item.key}
							label={item.label}
							span={'span' in item ? item?.span : undefined} // Assign the span from the item object
							labelStyle={'labelStyle' in item ? item.labelStyle : undefined}
							contentStyle={{
								// @ts-expect-error type
								textAlign: getTextAlign(
									'children' in item ? item.children : undefined,
								),
								...('contentStyle' in item ? item.contentStyle : {}),
							}}
						>
							{'children' in item ? item.children : undefined}
						</Descriptions.Item>
					))}
				</Descriptions>
			) : null}
		</div>
	);
};

export default SimulationPreviewContent;
